import ufeApi from 'services/api/ufeApi';
import selfReturnUtils from 'utils/SelfReturn';
import { gqlClient } from 'services/api/gql/gqlClient';
import { DeleteSampleFromReplacementOrder } from 'constants/gql/mutations/checkout/deleteSampleFromReplacementOrder.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Delete+Item+From+Self-service+NCR+Order

/**
 * Deletes a sample from a replacement (NCR) order.
 * Routes to OIS GraphQL (DeleteSampleFromReplacementOrder) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {string} orderId - The replacement order ID
 * @param {string} skuId - The SKU ID of the sample to delete
 * @returns {Promise<Object>} The delete sample response
 * @throws {GraphQLException} For OIS GraphQL path: thrown by gqlClient.query() on network/GraphQL transport errors
 * @throws {Error} For OIS GraphQL path: thrown with errors array when mutation response includes errors field
 * @throws {Error} For OIS GraphQL path: thrown when mutation returns non-SUCCESS responseCode
 * @throws {Object} For OXS REST path: raw error object (handled by ufeApi.makeRequest rejection)
 */
const deleteItemFromOrder = async (orderId, skuId) => {
    const originatingOrderId = selfReturnUtils.getOriginalOrderId();

    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;

    if (globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'DeleteSampleFromReplacementOrder',
            query: DeleteSampleFromReplacementOrder,
            variables: {
                input: {
                    originalOrderId: originatingOrderId,
                    replacementOrderId: orderId,
                    skuId
                }
            }
        });

        const result = data?.deleteSampleFromReplacementOrder;

        if (!result) {
            throw new Error('Delete sample from replacement order: unexpected response shape');
        }

        if (result.errors?.length) {
            const error = new Error(result.errors[0]?.message || 'Delete sample from replacement order failed');
            error.errorCode = -1;
            error.errorMessages = result.errors.map(e => e?.message).filter(Boolean);
            error.errors = result.errors;
            throw error;
        }

        if (result.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Delete sample from replacement order failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];
            throw error;
        }

        return result;
    }

    const originalOrderIdParam = originatingOrderId ? `?originalOrderId=${originatingOrderId}` : '';
    const url = `/api/selfReturn/order/${orderId}/items/${skuId}` + originalOrderIdParam;

    return ufeApi.makeRequest(url, { method: 'DELETE' }, { skipLoginPrompt: true });
};

export default deleteItemFromOrder;
